import type { TaskTableDefinition, TaskTableFormState } from "../types/manualAssessmentWorkflow";
import {
  getTaskTableCellValue,
  getTaskTableFieldName,
  getTaskTableIndexValue,
  getTriageAssessmentRequiredStateKey,
  resolveColumnTaskInstanceId,
  tableSupportsAssessmentRequiredRows,
} from "./manualAssessmentTaskTable";

/** Backend / demo name for Overall Approve table. */
export const APPROVE_TABLE_NAME = "approve_table";

/** Backend / demo name for Overall Approve reopen table. */
export const REOPEN_TABLE_NAME = "reopen_table";

/** Overall Review table title returned by live Olympus BPMN (not `approve_table`). */
export const SME_ASSESSMENT_BY_CATEGORY_TABLE_NAME =
  "SME Assessment by Category";

/**
 * Triage / Identify SME: when every `assessment_required` row is off,
 * submit `taskResult` as `["skip_all"]` (mirrors Overall Approve `["approve_all"]`).
 */
export const SKIP_ALL_TASK_RESULT = "skip_all";

function isTruthyCheckboxValue(value: unknown): boolean {
  if (value === true) return true;
  if (value === false || value == null) return false;
  const normalized = String(value).trim().toLowerCase();
  return normalized === "true" || normalized === "yes" || normalized === "1";
}

function normalizeApproveDecision(value: unknown): string {
  return String(value ?? "").trim().toLowerCase();
}

function findApproveColumn(row: {
  columns?: Array<{ field?: { name?: string; field?: string } }>;
}) {
  return row.columns?.find(
    (column) => getTaskTableFieldName(column.field) === "approve",
  );
}

function findReopenColumn(row: {
  columns?: Array<{ field?: { name?: string; field?: string } }>;
}) {
  return row.columns?.find(
    (column) => getTaskTableFieldName(column.field) === "reopen_required",
  );
}

/** Overall-approve style table: classic `approve_table` or rows with virtual `approve`. */
export function tableHasApproveDecisionColumn(
  table: TaskTableDefinition,
): boolean {
  if (
    table.name === APPROVE_TABLE_NAME ||
    table.name === SME_ASSESSMENT_BY_CATEGORY_TABLE_NAME
  ) {
    return true;
  }
  return (table.rows ?? []).some((row) => Boolean(findApproveColumn(row)));
}

export function findOverallApproveTable(
  tables: readonly TaskTableDefinition[] | null | undefined,
): TaskTableDefinition | undefined {
  return (tables ?? []).find((table) => tableHasApproveDecisionColumn(table));
}

export function findOverallReopenTable(
  tables: readonly TaskTableDefinition[] | null | undefined,
  approveTable?: TaskTableDefinition,
): TaskTableDefinition | undefined {
  const named = findTaskTableByName(tables, REOPEN_TABLE_NAME);
  if (named) return named;
  return (tables ?? []).find(
    (table) =>
      table.name !== approveTable?.name &&
      (table.rows ?? []).some((row) => Boolean(findReopenColumn(row))),
  );
}

/** Companion “Add New SME…” / triage-like table that shows Select-all no-impact. */
export function findAssessmentRequiredCompanionTable(
  tables: readonly TaskTableDefinition[] | null | undefined,
  approveTable: TaskTableDefinition,
): TaskTableDefinition | undefined {
  return (tables ?? []).find(
    (table) =>
      table.name !== approveTable.name &&
      tableSupportsAssessmentRequiredRows(table),
  );
}

/** Triage: taskResult is departments where assessment_required is true. */
export function buildTriageTaskResultFromTable(
  table: TaskTableDefinition,
  state: TaskTableFormState,
): string[] {
  const departments: string[] = [];

  for (const row of table.rows ?? []) {
    const department = getTaskTableIndexValue(table, row);
    if (!department) continue;

    const assessmentColumn = row.columns?.find(
      (column) => getTaskTableFieldName(column.field) === "assessment_required",
    );
    if (!assessmentColumn) continue;

    const value = getTaskTableCellValue(
      state,
      table.name,
      getTriageAssessmentRequiredStateKey(table, row),
      "assessment_required",
      assessmentColumn.field?.fieldValue,
    );

    if (isTruthyCheckboxValue(value)) {
      departments.push(department);
    }
  }

  return departments;
}

/**
 * Overall Approve §4.5: derive taskResult from virtual columns
 * - `approve` in approve table → `Reject:{department}` when reject
 * - `reopen_required` in reopen_table → `Reopen:{department}` when true
 * - companion `assessment_required` (Add New SME…) → `Reopen:{department}` when true
 * - merged empty → `["approve_all"]`
 */
export function buildOverallApproveTaskResultFromTables(
  approveTable: TaskTableDefinition,
  reopenTable: TaskTableDefinition | null | undefined,
  state: TaskTableFormState,
  companionTable?: TaskTableDefinition | null,
): string[] {
  const results: string[] = [];
  const reopenTokens = new Set<string>();

  for (const row of approveTable.rows ?? []) {
    const department = getTaskTableIndexValue(approveTable, row);
    if (!department) continue;

    const approveColumn = findApproveColumn(row);
    if (!approveColumn) continue;

    const decision = normalizeApproveDecision(
      getTaskTableCellValue(
        state,
        approveTable.name,
        resolveColumnTaskInstanceId(row, approveColumn),
        "approve",
        approveColumn.field?.fieldValue,
      ),
    );

    if (decision === "reject") {
      results.push(`Reject:${department}`);
    }
  }

  for (const row of reopenTable?.rows ?? []) {
    const department = getTaskTableIndexValue(reopenTable!, row);
    if (!department) continue;

    const reopenColumn = findReopenColumn(row);
    if (!reopenColumn) continue;

    const value = getTaskTableCellValue(
      state,
      reopenTable!.name,
      resolveColumnTaskInstanceId(row, reopenColumn),
      "reopen_required",
      reopenColumn.field?.fieldValue,
    );

    if (isTruthyCheckboxValue(value)) {
      reopenTokens.add(`Reopen:${department}`);
    }
  }

  // Olympus live shape: companion Add New SME uses assessment_required as Reopen source.
  if (
    companionTable &&
    companionTable.name !== reopenTable?.name &&
    companionTable.name !== approveTable.name
  ) {
    for (const department of buildTriageTaskResultFromTable(
      companionTable,
      state,
    )) {
      reopenTokens.add(`Reopen:${department}`);
    }
  }

  results.push(...reopenTokens);

  if (results.length === 0) {
    return ["approve_all"];
  }

  return results;
}

/** Triage departments historically submitted as a comma-joined string. */
export function taskResultArrayToSubmitString(values: string[]): string {
  return values.join(",");
}

export function findTaskTableByName(
  tables: readonly TaskTableDefinition[] | null | undefined,
  name: string,
): TaskTableDefinition | undefined {
  return (tables ?? []).find((table) => table.name === name);
}

/**
 * Triage / Identify SME `taskResult`:
 * - any `assessment_required` on → comma-joined department ids
 * - all off → `["skip_all"]` (same shape as Overall Approve `["approve_all"]`)
 */
function resolveAssessmentRequiredTaskResult(
  table: TaskTableDefinition,
  state: TaskTableFormState,
): string | string[] {
  const departments = buildTriageTaskResultFromTable(table, state);
  if (departments.length === 0) {
    return [SKIP_ALL_TASK_RESULT];
  }
  return taskResultArrayToSubmitString(departments);
}

/**
 * Resolve submit `taskResult` from task tables.
 *
 * - Overall Approve / Overall Review: returns `string[]`
 *   (`["approve_all"]` or `["Reject:…","Reopen:…"]`) per §4.5
 * - Triage / Identify SME: comma-joined department string, or
 *   `["skip_all"]` when every `assessment_required` is off
 */
export function resolveTaskResultFromTaskTables(
  tables: readonly TaskTableDefinition[] | null | undefined,
  state: TaskTableFormState,
): string | string[] | null {
  if (!tables?.length) return null;

  // Prefer approve-style tables over assessment_required companions (Overall Review).
  const approveTable = findOverallApproveTable(tables);
  if (approveTable) {
    const reopenTable = findOverallReopenTable(tables, approveTable);
    const companionTable = findAssessmentRequiredCompanionTable(
      tables,
      approveTable,
    );
    return buildOverallApproveTaskResultFromTables(
      approveTable,
      reopenTable,
      state,
      companionTable,
    );
  }

  const triageTable = findTaskTableByName(tables, "triage_table");
  if (triageTable) {
    return resolveAssessmentRequiredTaskResult(triageTable, state);
  }

  const assessmentTable = tables.find((table) =>
    tableSupportsAssessmentRequiredRows(table),
  );
  if (assessmentTable) {
    return resolveAssessmentRequiredTaskResult(assessmentTable, state);
  }

  return null;
}
